"use client";

import { useRef } from "react";
import { X } from "lucide-react";
import type { SalesProject } from "@/types";
import { workflowStages } from "@/assets/mock";
import ProjectPDF from "./ProjectPDF";

interface ProjectPreviewProps {
  project: SalesProject;
  onClose: () => void;
}

const ProjectPreview = ({ project, onClose }: ProjectPreviewProps) => {
  const printRef = useRef<HTMLDivElement>(null);

  const getStageDetails = (stageNumber: number) => {
    return workflowStages.find((s) => s.stage_number === stageNumber);
  };

  const currentStage = getStageDetails(project.current_stage);

  return (
    <>
      <div className="hidden md:flex fixed inset-0 bg-black bg-opacity-50 z-[100000] items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto flex flex-col">
          <div className="sticky top-0 bg-white border-b border-secondary/20 p-6 flex items-center justify-between flex-shrink-0">
            <h2 className="text-lg font-bold text-gray-900">
              Project Overview
            </h2>
            <div className="flex items-center gap-2">
              <ProjectPDF project={project} currentStage={currentStage} />
              <button
                onClick={onClose}
                className="p-2 rounded-full bg-red-600 text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div ref={printRef} className="p-6 flex-1 flex flex-col">
            <div className="flex-1">
              <div className="mb-6">
                <h1 className="text-lg font-bold text-gray-900 mb-1">
                  {project.name}
                </h1>
                <p className="text-xs text-primary">{project.client}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Start Date</p>
                  <p className="text-xs text-gray-900">{project.start_date}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">
                    Expected Completion
                  </p>
                  <p className="text-xs text-gray-900">
                    {project.expected_completion}
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-bold text-gray-900">
                    Overall Progress
                  </h3>
                  <span className="text-xs font-medium text-gray-900">
                    {project.progress}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-primary h-1.5 rounded-full"
                    style={{ width: `${project.progress}%` }}
                  />
                </div>
              </div>

              {currentStage && (
                <div className="border border-secondary/20 rounded p-4">
                  <h3 className="text-xs font-bold text-gray-900 mb-3">
                    Current Stage
                  </h3>

                  <div className="mb-3">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                      <div className="flex justify-between w-full items-center">
                        <h4 className="text-xs font-semibold text-gray-900">
                          Stage {currentStage.stage_number}: {currentStage.name}
                        </h4>

                        <p className="text-xs text-primary items-center">
                          {currentStage.timeline}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-700">
                      {currentStage.description}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Stage Owner</p>
                      <p className="text-xs text-gray-900">
                        {currentStage.owner}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Rationale</p>
                      <p className="text-xs text-gray-900">
                        {currentStage.rationale}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-auto pt-6 border-t border-secondary/20">
              <div className="flex justify-between text-xs text-gray-500">
                <p>Generated on {new Date().toLocaleDateString()}</p>
                <p>Project Management System</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-[100000]">
        <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-lg max-h-[90vh] overflow-y-auto flex flex-col">
          <div className="sticky top-0 bg-white border-b border-secondary/20 p-4 flex items-center justify-between flex-shrink-0">
            <h2 className="text-lg font-bold text-gray-900">
              Project Overview
            </h2>
            <div className="flex items-center gap-2">
              <ProjectPDF project={project} currentStage={currentStage} />
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-4 flex-1 flex flex-col">
            <div className="flex-1">
              <div className="mb-4">
                <h1 className="text-lg font-bold text-gray-900 mb-1">
                  {project.name}
                </h1>
                <p className="text-xs text-primary">{project.client}</p>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-bold text-gray-900">Progress</h3>
                  <span className="text-xs font-medium text-gray-900">
                    {project.progress}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-primary h-1.5 rounded-full"
                    style={{ width: `${project.progress}%` }}
                  />
                </div>
              </div>

              {currentStage && (
                <div className="border border-secondary/20 rounded p-3">
                  <h3 className="text-xs font-bold text-gray-900 mb-2">
                    Current Stage
                  </h3>

                  <div className="mb-2">
                    <div className="flex items-center gap-1.5 mb-1">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                      <h4 className="text-xs font-semibold text-gray-900">
                        Stage {currentStage.stage_number}: {currentStage.name}
                      </h4>
                    </div>
                    <p className="text-xs text-primary mb-1">
                      {currentStage.timeline}
                    </p>
                    <p className="text-xs text-gray-700">
                      {currentStage.description}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div>
                      <p className="text-xs text-gray-500">Stage Owner</p>
                      <p className="text-xs text-gray-900">
                        {currentStage.owner}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Rationale</p>
                      <p className="text-xs text-gray-900">
                        {currentStage.rationale}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 pt-4 border-t border-secondary/20">
              <div className="flex justify-between text-xs text-gray-500">
                <p>Generated on {new Date().toLocaleDateString()}</p>
                <p>Project Management System</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectPreview;